/**
 * AppointmentHoverDetails
 * ──────────────────────────────────────────────────────────────────────
 * Rich, CLICK-THROUGH EDITABLE HoverCard for appointment tiles.
 *
 * - Default: read-only summary of the appointment.
 * - "Quick edit" pencil toggles inline edit controls for time, provider,
 *   room, and status. Save emits onQuickEdit({patchedApt}). Cancel reverts.
 *
 * Appears on hover/focus anywhere we wrap an appointment tile — used in
 * Today grid, Today list, Calendar Day grid.
 */
import { useState, useEffect } from 'react';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../ui/hover-card';
import {
  Clock, MapPin, Stethoscope, Shield, AlertTriangle, User, Phone,
  Pencil, Check, X,
} from 'lucide-react';
import { cn } from '../../../../lib/utils';

const formatTime = (t) => {
  if (!t) return '—';
  const [hh, mm = '00'] = t.split(':');
  const h = parseInt(hh);
  if (h === 0) return `12:${mm} AM`;
  if (h === 12) return `12:${mm} PM`;
  return h > 12 ? `${h - 12}:${mm} PM` : `${h}:${mm} AM`;
};

const statusLabel = {
  confirmed: { label: 'Confirmed', color: 'text-emerald-600', dot: '#22c55e' },
  scheduled: { label: 'Scheduled', color: 'text-slate-600', dot: '#94a3b8' },
  flagged: { label: 'Insurance flagged', color: 'text-red-600', dot: '#ef4444' },
  at_risk: { label: 'At risk', color: 'text-red-600', dot: '#ef4444' },
  in_exam: { label: 'In exam', color: 'text-blue-600', dot: '#3b82f6' },
  checked_out: { label: 'Checked out', color: 'text-slate-500', dot: '#94a3b8' },
  open: { label: 'Open slot', color: 'text-slate-400', dot: '#d1d5db' },
};

const STATUS_OPTIONS = [
  { id: 'scheduled', label: 'Scheduled' },
  { id: 'confirmed', label: 'Confirmed' },
  { id: 'flagged', label: 'Flagged' },
  { id: 'at_risk', label: 'At risk' },
  { id: 'in_exam', label: 'In exam' },
  { id: 'checked_out', label: 'Checked out' },
];

const TIME_OPTIONS = [
  '8:00', '8:30', '9:00', '9:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
];

const ROOM_OPTIONS = ['', 'Room 1', 'Room 2', 'Room 3', 'Exam A', 'Exam B', 'Telehealth'];

const AppointmentHoverDetails = ({
  apt,
  providerName,
  providerColor,
  isOverbook = false,
  side = 'right',
  delay = 200,
  children,
  testId,
  providers = [],           // [{ id, name, color }]
  onQuickEdit,              // ({ patchedApt }) => void
  editable = true,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  // Reset draft whenever apt changes or we enter edit mode.
  useEffect(() => {
    if (editing && apt) {
      setDraft({
        time: apt.time || '9:00',
        provider: apt.provider || providers[0]?.id || '',
        room: apt.room || '',
        status: apt.status || 'scheduled',
      });
    } else if (!editing) {
      setDraft(null);
    }
  }, [editing, apt, providers]);

  if (!apt) return children;
  const s = statusLabel[apt.status] || statusLabel.scheduled;

  const handleSave = () => {
    if (!onQuickEdit) { setEditing(false); return; }
    onQuickEdit({
      patchedApt: {
        ...apt,
        time: draft.time,
        provider: draft.provider,
        room: draft.room || null,
        status: draft.status,
      },
    });
    setEditing(false);
  };

  return (
    <HoverCard openDelay={delay} closeDelay={120}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side={side}
        align="start"
        className="w-80 p-0 overflow-hidden"
        data-testid={testId || `hover-${apt.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-3 py-2 border-b border-slate-100 bg-white flex items-start gap-2">
          <div className="h-8 w-8 rounded-full bg-slate-100 inline-flex items-center justify-center shrink-0">
            <User className="h-3.5 w-3.5 text-slate-500" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="text-[12px] font-semibold text-slate-900 truncate">{apt.patient || 'Open slot'}</p>
              {isOverbook && (
                <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white inline-flex items-center gap-0.5">
                  <AlertTriangle className="h-2 w-2" /> OB
                </span>
              )}
            </div>
            <p className={cn('text-[9px] mt-0.5 inline-flex items-center gap-1', s.color)}>
              <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: s.dot }} />
              {(editing ? (STATUS_OPTIONS.find(o => o.id === draft?.status)?.label) : s.label) || s.label}
            </p>
          </div>
          {editable && !editing && (
            <button
              onClick={(e) => { e.stopPropagation(); setEditing(true); }}
              className="h-6 w-6 rounded-md inline-flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              aria-label="Quick edit"
              data-testid={`hover-edit-${apt.id}`}
              title="Quick edit"
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}
          {editing && (
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => { e.stopPropagation(); setEditing(false); }}
                className="h-6 w-6 rounded-md inline-flex items-center justify-center text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                aria-label="Cancel"
                data-testid={`hover-cancel-${apt.id}`}
              >
                <X className="h-3 w-3" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); handleSave(); }}
                className="h-6 w-6 rounded-md inline-flex items-center justify-center bg-[#111] text-white hover:bg-[#222]"
                style={{ border: 'none', cursor: 'pointer' }}
                aria-label="Save changes"
                data-testid={`hover-save-${apt.id}`}
              >
                <Check className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        {/* READ BODY */}
        {!editing && (
          <div className="px-3 py-2.5 space-y-1.5 text-[11px] bg-white">
            <div className="flex items-center gap-1.5 text-slate-700">
              <Clock className="h-3 w-3 text-slate-400" />
              <span>{formatTime(apt.time)}{apt.endTime ? ` – ${formatTime(apt.endTime)}` : ''}</span>
              <span className="text-slate-400">· {apt.durationMin || 30}m</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-700">
              <Stethoscope className="h-3 w-3 text-slate-400" />
              <span>{apt.type || '—'}</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-700">
              <span className="inline-block h-2 w-2 rounded-full" style={{ background: providerColor || '#94a3b8' }} />
              <span>{providerName || apt.provider || '—'}</span>
            </div>
            {apt.room && (
              <div className="flex items-center gap-1.5 text-slate-700">
                <MapPin className="h-3 w-3 text-slate-400" />
                <span>{apt.room}</span>
              </div>
            )}
            {apt.phone && (
              <div className="flex items-center gap-1.5 text-slate-700">
                <Phone className="h-3 w-3 text-slate-400" />
                <span>{apt.phone}</span>
              </div>
            )}
            {typeof apt.copay === 'number' && apt.copay > 0 && (
              <div className="flex items-center gap-1.5 text-amber-600">
                <Shield className="h-3 w-3" />
                <span>${apt.copay} copay due</span>
              </div>
            )}
            {typeof apt.noShow === 'number' && apt.noShow > 30 && (
              <div className="flex items-center gap-1.5 text-red-600">
                <AlertTriangle className="h-3 w-3" />
                <span>{apt.noShow}% no-show risk</span>
              </div>
            )}
          </div>
        )}

        {/* EDIT BODY */}
        {editing && draft && (
          <div className="px-3 py-2.5 space-y-2 bg-white" data-testid={`hover-edit-body-${apt.id}`}>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[9px] text-slate-400 uppercase font-semibold">Time</label>
                <select
                  value={draft.time}
                  onChange={(e) => setDraft({ ...draft, time: e.target.value })}
                  className="w-full mt-0.5 px-2 py-1.5 rounded-md border border-slate-200 text-[11px] outline-none bg-slate-50"
                  data-testid={`hover-edit-time-${apt.id}`}
                >
                  {TIME_OPTIONS.map((t) => (
                    <option key={t} value={t}>{formatTime(t)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[9px] text-slate-400 uppercase font-semibold">Provider</label>
                <select
                  value={draft.provider}
                  onChange={(e) => setDraft({ ...draft, provider: e.target.value })}
                  className="w-full mt-0.5 px-2 py-1.5 rounded-md border border-slate-200 text-[11px] outline-none bg-slate-50"
                  data-testid={`hover-edit-provider-${apt.id}`}
                >
                  {providers.length === 0 && <option value={apt.provider}>{providerName || apt.provider}</option>}
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[9px] text-slate-400 uppercase font-semibold">Room</label>
                <select
                  value={draft.room || ''}
                  onChange={(e) => setDraft({ ...draft, room: e.target.value })}
                  className="w-full mt-0.5 px-2 py-1.5 rounded-md border border-slate-200 text-[11px] outline-none bg-slate-50"
                  data-testid={`hover-edit-room-${apt.id}`}
                >
                  {ROOM_OPTIONS.map((r) => (
                    <option key={r || 'none'} value={r}>{r || 'Unassigned'}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[9px] text-slate-400 uppercase font-semibold">Status</label>
                <select
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                  className="w-full mt-0.5 px-2 py-1.5 rounded-md border border-slate-200 text-[11px] outline-none bg-slate-50"
                  data-testid={`hover-edit-status-${apt.id}`}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.id} value={o.id}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Footer hint */}
        <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50 text-[9px] text-slate-400 flex items-center justify-between">
          <span>{editing ? 'Edit time, provider, room or status' : 'Click tile to open full details'}</span>
          {!editing && editable && <span className="text-[9px] text-slate-500">✏ to quick edit</span>}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

export default AppointmentHoverDetails;
